/**
 * Slash Command System for TerraMatrix AI Chat
 * Provides instant command execution without AI backend calls
 */

import { Component } from "@/types";
import { CommandResult, PropertyChange } from "./BaseCommandHandler";
import {
  slashCommands,
  SlashCommand,
  CommandCategory,
  SlashCommandContext,
} from "./slashCommands";
import { useEditorStore } from "@/stores/editor/editor-store";

export interface ParsedCommand {
  command: string;
  args: string[];
  rawArgs: string;
}

export interface CommandSegment {
  value: string;
  description?: string;
  hasChildren: boolean;
  fullCommand?: string;
  category?: CommandCategory;
}

export class SlashCommandSystem {
  private static instance: SlashCommandSystem;
  private commands: Map<string, SlashCommand> = new Map();
  private aliases: Map<string, string> = new Map();

  private constructor() {
    this.registerCommands();
  }

  static getInstance(): SlashCommandSystem {
    if (!SlashCommandSystem.instance) {
      SlashCommandSystem.instance = new SlashCommandSystem();
    }
    return SlashCommandSystem.instance;
  }

  /**
   * Register all slash commands
   */
  private registerCommands(): void {
    slashCommands.forEach(command => {
      // Register main command
      this.commands.set(command.name.toLowerCase(), command);

      // Register aliases
      command.aliases.forEach(alias => {
        this.aliases.set(alias.toLowerCase(), command.name.toLowerCase());
      });
    });
  }


  /**
   * Check if input is a slash command
   */
  isSlashCommand(input: string): boolean {
    return input.trim().startsWith('/');
  }

  /**
   * Parse command string into command and arguments
   * Handles multi-word commands by trying to match the longest possible command first
   * Strips common articles (a, an, the) between command and arguments
   */
  private parseCommand(input: string): ParsedCommand {
    const trimmed = input.trim();
    if (!trimmed.startsWith('/')) {
      return { command: '', args: [], rawArgs: '' };
    }

    // Remove leading slash
    const withoutSlash = trimmed.substring(1);

    // Split by spaces but respect quoted strings
    const parts = this.splitCommandString(withoutSlash);

    // Try to find the longest matching command (progressive matching)
    let commandName = '';
    let commandArgs: string[] = [];

    // Start with the full string and work backwards
    for (let i = parts.length; i > 0; i--) {
      const potentialCommand = parts.slice(0, i).join(' ').toLowerCase();

      // Check if this potential command exists (either as main command or alias)
      if (this.commands.has(potentialCommand) || this.aliases.has(potentialCommand)) {
        commandName = potentialCommand;
        commandArgs = parts.slice(i);
        break;
      }
    }

    // If no multi-word command found, fall back to single word
    if (!commandName && parts.length > 0) {
      commandName = parts[0].toLowerCase();
      commandArgs = parts.slice(1);
    }

    // Strip common articles from the beginning of arguments
    // This allows "/add a chart" to work the same as "/add chart"
    const articles = ['a', 'an', 'the'];
    while (commandArgs.length > 0 && articles.includes(commandArgs[0].toLowerCase())) {
      commandArgs = commandArgs.slice(1);
    }

    return {
      command: commandName,
      args: commandArgs,
      rawArgs: commandArgs.join(' ')
    };
  }

  /**
   * Split command string respecting quoted strings
   */
  private splitCommandString(str: string): string[] {
    const parts: string[] = [];
    let current = '';
    let inQuotes = false;
    let quoteChar = '';

    for (let i = 0; i < str.length; i++) {
      const char = str[i];

      if ((char === '"' || char === "'") && !inQuotes) {
        inQuotes = true;
        quoteChar = char;
      } else if (char === quoteChar && inQuotes) {
        inQuotes = false;
        quoteChar = '';
      } else if (char === ' ' && !inQuotes) {
        if (current) {
          parts.push(current);
          current = '';
        }
      } else {
        current += char;
      }
    }

    if (current) {
      parts.push(current);
    }

    return parts;
  }

  /**
   * Execute a slash command
   */
  async execute(
    input: string,
    component?: Component,
    context?: SlashCommandContext,
  ): Promise<CommandResult> {
    const parsed = this.parseCommand(input);

    if (!parsed.command) {
      return {
        success: false,
        action: 'unknown',
        error: 'Invalid command format'
      };
    }

    // Handle help command specially
    if (parsed.command === 'help' || parsed.command === '?') {
      return this.handleHelp(parsed.args);
    }

    // Find command (check aliases first)
    const commandName = this.aliases.get(parsed.command) || parsed.command;
    const command = this.commands.get(commandName);

    if (!command) {
      // Return suggestions for unknown commands
      const suggestions = this.getSuggestions(parsed.command);
      return {
        success: false,
        action: 'unknown',
        error: `Unknown command: /${parsed.command}`,
        suggestions: suggestions.map(s => `/${s}`)
      };
    }

    try {
      // Execute the command handler
      const result = await command.handler(parsed.args, component, context);

      // Wrap the handler result in our CommandResult format
      if ('success' in result) {
        return result as CommandResult;
      }

      // Legacy handler format - convert to CommandResult
      return {
        success: true,
        action: this.determineAction(command),
        changes: result as PropertyChange,
        message: `Applied /${command.name}`
      };
    } catch (error: unknown) {
      return {
        success: false,
        action: 'unknown',
        error: error instanceof Error ? error.message : `Failed to execute /${command.name}`
      };
    }
  }

  /**
   * Determine action type from command category
   */
  private determineAction(command: SlashCommand): CommandResult['action'] {
    switch (command.category) {
      case 'component':
        return 'add';
      case 'template':
        return 'template';
      case 'action':
        return command.name === 'delete' ? 'delete' : 'modify';
      default:
        return 'modify';
    }
  }

  /**
   * Handle help command
   */
  private handleHelp(args: string[]): CommandResult {
    if (args.length === 0) {
      // Show all commands grouped by category
      const categories: Record<CommandCategory, SlashCommand[]> = {
        style: [],
        layout: [],
        component: [],
        action: [],
        data: [],
        template: []
      };

      this.commands.forEach(command => {
        categories[command.category].push(command);
      });

      let helpText = "# Available Slash Commands\n\n";

      Object.entries(categories).forEach(([category, commands]) => {
        if (commands.length > 0) {
          helpText += `## ${category.charAt(0).toUpperCase() + category.slice(1)} Commands\n\n`;
          commands.forEach(cmd => {
            helpText += `- **/${cmd.name}** - ${cmd.description}`;
            if (cmd.aliases.length > 0) {
              helpText += ` (aliases: ${cmd.aliases.map(a => `/${a}`).join(', ')})`;
            }
            helpText += '\n';
          });
          helpText += '\n';
        }
      });

      return {
        success: true,
        action: 'help',
        message: helpText
      };
    }

    // Show help for specific command
    const commandName = args[0].replace('/', '');
    const resolvedName = this.aliases.get(commandName) || commandName;
    const command = this.commands.get(resolvedName);

    if (!command) {
      return {
        success: false,
        action: 'help',
        error: `Command not found: /${commandName}`,
        suggestions: this.getSuggestions(commandName).map(s => `/${s}`)
      };
    }

    let helpText = `# /${command.name}\n\n`;
    helpText += `${command.description}\n\n`;

    if (command.aliases.length > 0) {
      helpText += `**Aliases:** ${command.aliases.map(a => `/${a}`).join(', ')}\n\n`;
    }

    if (command.parameters && command.parameters.length > 0) {
      helpText += `**Parameters:**\n`;
      command.parameters.forEach(param => {
        helpText += `- **${param.name}** (${param.type}${param.required ? ', required' : ', optional'}): ${param.description}\n`;
      });
      helpText += '\n';
    }

    if (command.examples && command.examples.length > 0) {
      helpText += `**Examples:**\n`;
      command.examples.forEach(example => {
        helpText += `- \`${example}\`\n`;
      });
    }

    return {
      success: true,
      action: 'help',
      message: helpText
    };
  }

  /**
   * Get command suggestions based on partial input
   * Handles multi-word command suggestions intelligently
   */
  getSuggestions(partial: string): string[] {
    const lower = partial.toLowerCase();
    const suggestions: string[] = [];
    const seen = new Set<string>();

    // Split the partial to handle multi-word matching
    const parts = lower.split(' ');

    // Check main commands
    this.commands.forEach((_, name) => {
      // Check if the command starts with the partial input
      if (name.startsWith(lower)) {
        if (!seen.has(name)) {
          suggestions.push(name);
          seen.add(name);
        }
      } else if (parts.length > 1) {
        // For multi-word input, also check if command contains all parts
        const commandParts = name.split(' ');
        let matches = true;
        for (let i = 0; i < parts.length && i < commandParts.length; i++) {
          if (!commandParts[i].startsWith(parts[i])) {
            matches = false;
            break;
          }
        }
        if (matches && !seen.has(name)) {
          suggestions.push(name);
          seen.add(name);
        }
      }
    });

    // Check aliases with the same logic
    this.aliases.forEach((target, alias) => {
      if (alias.startsWith(lower)) {
        if (!seen.has(alias)) {
          suggestions.push(alias);
          seen.add(alias);
        }
      } else if (parts.length > 1) {
        const aliasParts = alias.split(' ');
        let matches = true;
        for (let i = 0; i < parts.length && i < aliasParts.length; i++) {
          if (!aliasParts[i].startsWith(parts[i])) {
            matches = false;
            break;
          }
        }
        if (matches && !seen.has(alias)) {
          suggestions.push(alias);
          seen.add(alias);
        }
      }
    });

    // Sort suggestions to prioritize exact prefix matches
    suggestions.sort((a, b) => {
      // Exact match comes first
      if (a === lower) return -1;
      if (b === lower) return 1;

      // Then prioritize shorter commands
      if (a.length !== b.length) return a.length - b.length;

      // Finally alphabetical
      return a.localeCompare(b);
    });

    return suggestions.slice(0, 10); // Limit to 10 suggestions
  }

  /**
   * Get component type suggestions for the /add command
   */
  private getComponentTypeSuggestions(partial: string): CommandSegment[] {
    const componentTypes = [
      { value: 'add text', description: 'Add a text component', category: 'component' as CommandCategory },
      { value: 'add data text', description: 'Add a data-bound text component', category: 'component' as CommandCategory },
      { value: 'add button', description: 'Add a button component', category: 'component' as CommandCategory },
      { value: 'add input', description: 'Add an input field component', category: 'component' as CommandCategory },
      { value: 'add image', description: 'Add an image component', category: 'component' as CommandCategory },
      { value: 'add container', description: 'Add a container component', category: 'component' as CommandCategory },
      { value: 'add line chart', description: 'Add a line chart component', category: 'component' as CommandCategory },
      { value: 'add bar chart', description: 'Add a bar chart component', category: 'component' as CommandCategory },
      { value: 'add pie chart', description: 'Add a pie chart component', category: 'component' as CommandCategory },
      { value: 'add area chart', description: 'Add an area chart component', category: 'component' as CommandCategory },
      { value: 'add scatter chart', description: 'Add a scatter chart component', category: 'component' as CommandCategory },
      { value: 'add heatmap chart', description: 'Add a heatmap chart component', category: 'component' as CommandCategory },
      { value: 'add treemap chart', description: 'Add a treemap chart component', category: 'component' as CommandCategory },
      { value: 'add gauge chart', description: 'Add a gauge chart component', category: 'component' as CommandCategory },
      { value: 'add vertical-gauge chart', description: 'Add a vertical tank gauge component', category: 'component' as CommandCategory },
      { value: 'add radial dial', description: 'Add an interactive radial dial control', category: 'component' as CommandCategory },
      { value: 'add map', description: 'Add a soil moisture map component', category: 'component' as CommandCategory },
      { value: 'add map chart', description: 'Add a soil moisture map component', category: 'component' as CommandCategory },
      { value: 'add table', description: 'Add a table component', category: 'component' as CommandCategory },
      { value: 'add file storage', description: 'Add a file storage component', category: 'component' as CommandCategory },
      { value: 'add kanban board', description: 'Add a kanban board for organizing tasks into columns', category: 'component' as CommandCategory },
      { value: 'add toggle switch', description: 'Add an on/off toggle switch control', category: 'component' as CommandCategory },
      { value: 'add push button', description: 'Add a momentary push button for triggering actions', category: 'component' as CommandCategory },
      { value: 'add icon button', description: 'Add an icon-based button with optional label', category: 'component' as CommandCategory },
      { value: 'add status light', description: 'Add a status indicator light (ok/warning/critical/offline)', category: 'component' as CommandCategory },
      { value: 'add slider', description: 'Add a range slider control for numeric values', category: 'component' as CommandCategory },
      { value: 'add stepper', description: 'Add a plus/minus stepper for incrementing values', category: 'component' as CommandCategory },
      { value: 'add level indicator', description: 'Add a segmented level indicator (battery/signal style)', category: 'component' as CommandCategory },
      { value: 'add segmented control', description: 'Add a multi-option segmented selector', category: 'component' as CommandCategory },
      { value: 'add color picker', description: 'Add a color selection control (wheel/strip/palette)', category: 'component' as CommandCategory },
      { value: 'add thermostat', description: 'Add a thermostat with temperature and mode control', category: 'component' as CommandCategory },
      { value: 'add modal', description: 'Add a modal dialog container', category: 'component' as CommandCategory },
      { value: 'add checklist', description: 'Add an interactive checklist with progress tracking', category: 'component' as CommandCategory },
      { value: 'add timeline', description: 'Add a milestone timeline for project phases', category: 'component' as CommandCategory },
      { value: 'add gantt chart', description: 'Add a Gantt chart for project scheduling with task bars and dependencies', category: 'component' as CommandCategory },
      { value: 'add calendar', description: 'Add an event calendar with month, week, day, and agenda views', category: 'component' as CommandCategory },
      { value: 'add assignment widget', description: 'Add a team assignment widget with avatars and status', category: 'component' as CommandCategory },
      { value: 'add questionnaire', description: 'Add a multi-step questionnaire with branching logic and response tracking', category: 'component' as CommandCategory },
      { value: 'add voting card', description: 'Add a voting/polling card with approve-veto or ranked voting', category: 'component' as CommandCategory },
    ];

    const lowerPartial = partial.toLowerCase();

    // If no partial input, show all component types
    if (!lowerPartial) {
      return componentTypes.map(type => ({
        value: type.value,
        description: type.description,
        hasChildren: false,
        fullCommand: type.value,
        category: type.category
      }));
    }

    // Filter component types based on partial match
    const filtered = componentTypes.filter(type => {
      const componentName = type.value.replace('add ', ''); // Remove "add " prefix for matching
      return componentName.startsWith(lowerPartial) || componentName.includes(lowerPartial);
    });

    // Sort by relevance (exact prefix match first, then contains)
    filtered.sort((a, b) => {
      const aName = a.value.replace('add ', '');
      const bName = b.value.replace('add ', '');
      const aStartsWith = aName.startsWith(lowerPartial);
      const bStartsWith = bName.startsWith(lowerPartial);

      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      return aName.localeCompare(bName);
    });

    return filtered.map(type => ({
      value: type.value,
      description: type.description,
      hasChildren: false,
      fullCommand: type.value,
      category: type.category
    }));
  }

  /**
   * Get page navigation suggestions for the /navigate command
   */
  private getPageSuggestions(partial: string): CommandSegment[] {
    const editorStore = useEditorStore.getState();
    const pages = editorStore.pages || [];
    const currentPageId = editorStore.currentPageId;

    // Filter out the current page (can't navigate to self)
    const navigablePages = pages.filter(page => page.id !== currentPageId);

    const lowerPartial = partial.toLowerCase();

    // If no partial input, show all navigable pages
    if (!lowerPartial) {
      return navigablePages.map(page => {
        const indicators = [];
        if (page.isDefault) indicators.push('default');
        const suffix = indicators.length > 0 ? ` (${indicators.join(', ')})` : '';

        return {
          value: `navigate ${page.name}`,
          description: `Navigate to ${page.name}${suffix}`,
          hasChildren: false,
          fullCommand: `navigate ${page.name}`,
          category: 'action' as CommandCategory
        };
      });
    }

    // Filter pages based on partial match
    const filtered = navigablePages.filter(page => {
      const pageName = page.name.toLowerCase();
      return pageName.startsWith(lowerPartial) || pageName.includes(lowerPartial);
    });

    // Sort by relevance (exact prefix match first, then contains)
    filtered.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      const aStartsWith = aName.startsWith(lowerPartial);
      const bStartsWith = bName.startsWith(lowerPartial);

      if (aStartsWith && !bStartsWith) return -1;
      if (!aStartsWith && bStartsWith) return 1;
      return aName.localeCompare(bName);
    });

    return filtered.map(page => {
      const indicators = [];
      if (page.isDefault) indicators.push('default');
      const suffix = indicators.length > 0 ? ` (${indicators.join(', ')})` : '';

      return {
        value: `navigate ${page.name}`,
        description: `Navigate to ${page.name}${suffix}`,
        hasChildren: false,
        fullCommand: `navigate ${page.name}`,
        category: 'action' as CommandCategory
      };
    });
  }

  /**
   * Get subcommands for commands that support them
   */
  private getSubcommands(commandName: string, partial: string = ''): CommandSegment[] {
    const lowerCommand = commandName.toLowerCase();
    const lowerPartial = partial.toLowerCase();

    // Define subcommands for each command
    const subcommandMap: Record<string, Array<{ value: string; description: string }>> = {
      'border': [
        { value: 'border width', description: 'Set border width (e.g., 2px)' },
        { value: 'border style', description: 'Set border style (solid, dashed, dotted, etc.)' },
        { value: 'border color', description: 'Set border color' },
        { value: 'border radius', description: 'Set border radius' },
        { value: 'border all', description: 'Set complete border (width style color)' },
        { value: 'border none', description: 'Remove border' }
      ],
      'bd': [
        { value: 'bd width', description: 'Set border width (e.g., 2px)' },
        { value: 'bd style', description: 'Set border style (solid, dashed, dotted, etc.)' },
        { value: 'bd color', description: 'Set border color' },
        { value: 'bd radius', description: 'Set border radius' },
        { value: 'bd all', description: 'Set complete border (width style color)' },
        { value: 'bd none', description: 'Remove border' }
      ],
      'shadow': [
        { value: 'shadow sm', description: 'Apply small shadow' },
        { value: 'shadow md', description: 'Apply medium shadow' },
        { value: 'shadow lg', description: 'Apply large shadow' },
        { value: 'shadow xl', description: 'Apply extra large shadow' },
        { value: 'shadow none', description: 'Remove shadow' },
        { value: 'shadow color', description: 'Set shadow color (requires value)' },
        { value: 'shadow blur', description: 'Set shadow blur radius (requires value)' },
        { value: 'shadow spread', description: 'Set shadow spread radius (requires value)' },
        { value: 'shadow offset', description: 'Set shadow offset (x,y) (requires value)' },
        { value: 'shadow custom', description: 'Set custom CSS shadow (requires value)' }
      ],
      'sh': [
        { value: 'sh sm', description: 'Apply small shadow' },
        { value: 'sh md', description: 'Apply medium shadow' },
        { value: 'sh lg', description: 'Apply large shadow' },
        { value: 'sh xl', description: 'Apply extra large shadow' },
        { value: 'sh none', description: 'Remove shadow' },
        { value: 'sh color', description: 'Set shadow color (requires value)' },
        { value: 'sh blur', description: 'Set shadow blur radius (requires value)' },
        { value: 'sh spread', description: 'Set shadow spread radius (requires value)' },
        { value: 'sh offset', description: 'Set shadow offset (x,y) (requires value)' },
        { value: 'sh custom', description: 'Set custom CSS shadow (requires value)' }
      ],
      'hover': [
        { value: 'hover text', description: 'Set hover text color' },
        { value: 'hover background', description: 'Set hover background color' },
        { value: 'hover bg', description: 'Set hover background color (alias)' }
      ],
      'hv': [
        { value: 'hv text', description: 'Set hover text color' },
        { value: 'hv background', description: 'Set hover background color' },
        { value: 'hv bg', description: 'Set hover background color (alias)' }
      ],
      'font': [
        { value: 'font weight', description: 'Set font weight (normal, bold, etc.)' },
        { value: 'font style', description: 'Set font style (normal, italic, etc.)' },
        { value: 'font size', description: 'Set font size' },
        { value: 'font family', description: 'Set font family' }
      ],
      'ft': [
        { value: 'ft weight', description: 'Set font weight (normal, bold, etc.)' },
        { value: 'ft style', description: 'Set font style (normal, italic, etc.)' },
        { value: 'ft size', description: 'Set font size' },
        { value: 'ft family', description: 'Set font family' }
      ],
      'file-storage': [
        { value: 'file-storage title', description: 'Set file storage title' },
        { value: 'file-storage mode', description: 'Set display mode (grid or list)' },
        { value: 'file-storage types', description: 'Set allowed file types (images, documents, spreadsheets, media, all)' },
        { value: 'file-storage uploads', description: 'Enable or disable uploads (on/off)' },
        { value: 'file-storage delete', description: 'Enable or disable file deletion (on/off)' },
        { value: 'file-storage search', description: 'Show or hide search bar (on/off)' },
        { value: 'file-storage thumbnails', description: 'Show or hide thumbnails (on/off)' },
        { value: 'file-storage quota', description: 'Show or hide quota bar (on/off)' },
        { value: 'file-storage max-size', description: 'Set max file size in MB' },
        { value: 'file-storage scope', description: 'Set default scope (project, organization, component)' }
      ],
      'fs': [
        { value: 'fs title', description: 'Set file storage title' },
        { value: 'fs mode', description: 'Set display mode (grid or list)' },
        { value: 'fs types', description: 'Set allowed file types (images, documents, spreadsheets, media, all)' },
        { value: 'fs uploads', description: 'Enable or disable uploads (on/off)' },
        { value: 'fs delete', description: 'Enable or disable file deletion (on/off)' },
        { value: 'fs search', description: 'Show or hide search bar (on/off)' },
        { value: 'fs thumbnails', description: 'Show or hide thumbnails (on/off)' },
        { value: 'fs quota', description: 'Show or hide quota bar (on/off)' },
        { value: 'fs max-size', description: 'Set max file size in MB' },
        { value: 'fs scope', description: 'Set default scope (project, organization, component)' }
      ],
      'files': [
        { value: 'files title', description: 'Set file storage title' },
        { value: 'files mode', description: 'Set display mode (grid or list)' },
        { value: 'files types', description: 'Set allowed file types (images, documents, spreadsheets, media, all)' },
        { value: 'files uploads', description: 'Enable or disable uploads (on/off)' },
        { value: 'files delete', description: 'Enable or disable file deletion (on/off)' },
        { value: 'files search', description: 'Show or hide search bar (on/off)' },
        { value: 'files thumbnails', description: 'Show or hide thumbnails (on/off)' },
        { value: 'files quota', description: 'Show or hide quota bar (on/off)' },
        { value: 'files max-size', description: 'Set max file size in MB' },
        { value: 'files scope', description: 'Set default scope (project, organization, component)' }
      ],
      'kanban-board': [
        { value: 'add column', description: 'Add a new column to the board' },
        { value: 'set wip limit', description: 'Set work-in-progress limit on a column' },
        { value: 'color code by', description: 'Color code cards by a field' },
        { value: 'card layout', description: 'Switch card layout (compact/standard/detailed)' },
      ],
      'gantt-chart': [
        { value: 'set time scale', description: 'Set time scale (day, week, month, quarter)' },
        { value: 'show dependencies', description: 'Show dependency arrows between tasks' },
        { value: 'group by', description: 'Group tasks into swimlanes by a field' },
        { value: 'show critical path', description: 'Highlight the critical path' },
        { value: 'show iteration bands', description: 'Show sprint/iteration background bands' },
      ],
      'calendar': [
        { value: 'switch to month view', description: 'Switch to month view' },
        { value: 'switch to week view', description: 'Switch to week view' },
        { value: 'switch to day view', description: 'Switch to day view' },
        { value: 'switch to agenda', description: 'Switch to agenda view' },
        { value: 'hide weekends', description: 'Hide weekend columns' },
        { value: 'show week numbers', description: 'Show week numbers' },
        { value: 'set business hours', description: 'Set business hours (e.g. 9am to 5pm)' },
        { value: 'color meetings blue', description: 'Color-code events by category' },
        { value: 'enable drag to reschedule', description: 'Enable drag-and-drop rescheduling' },
      ],
      'questionnaire': [
        { value: 'add step', description: 'Add a new step to the questionnaire' },
        { value: 'add question', description: 'Add a question to the current step' },
        { value: 'show results', description: 'Switch to results view' },
        { value: 'show form', description: 'Switch to form view' },
        { value: 'set accent color', description: 'Set the questionnaire accent color' },
        { value: 'set layout stacked', description: 'Use stacked question layout' },
        { value: 'set layout card', description: 'Use card question layout' },
      ],
      'voting-card': [
        { value: 'add option', description: 'Add a new voting option' },
        { value: 'set vote type', description: 'Change vote type (approve-veto, single-choice, ranked, thumbs)' },
        { value: 'set resolution', description: 'Set resolution mode (unanimous, majority, quorum)' },
        { value: 'set deadline', description: 'Set voting deadline' },
        { value: 'show results', description: 'Configure when results are visible' },
        { value: 'set layout list', description: 'Use list layout for options' },
        { value: 'set layout grid', description: 'Use grid layout for options' },
        { value: 'set layout card', description: 'Use card layout for options' },
      ],
      'modal': [
        { value: 'modal title', description: 'Set modal title text' },
        { value: 'modal size', description: 'Set modal size (sm, md, lg, full)' },
        { value: 'modal trigger', description: 'Set trigger button text' },
        { value: 'modal backdrop', description: 'Toggle backdrop overlay (on/off)' },
        { value: 'modal close-button', description: 'Toggle close button (on/off)' },
        { value: 'modal animation', description: 'Set animation type (fade, slide, none)' },
      ],
      'checklist': [
        { value: 'add item', description: 'Add a new checklist item' },
        { value: 'check all', description: 'Check all items' },
        { value: 'uncheck all', description: 'Uncheck all items' },
        { value: 'group by', description: 'Group items by field' },
        { value: 'show progress', description: 'Toggle progress header' },
      ],
      'horizontal-timeline': [
        { value: 'add event', description: 'Add a new timeline event' },
        { value: 'set variant milestone', description: 'Switch to milestone view' },
        { value: 'set variant gantt', description: 'Switch to gantt view' },
        { value: 'set orientation', description: 'Set horizontal or vertical' },
        { value: 'toggle dates', description: 'Show/hide date labels' },
      ],
      'timeline': [
        { value: 'add event', description: 'Add a new timeline event' },
        { value: 'set variant milestone', description: 'Switch to milestone view' },
        { value: 'set variant gantt', description: 'Switch to gantt view' },
      ],
      'assignment-widget': [
        { value: 'add person', description: 'Add a team member' },
        { value: 'set layout list', description: 'Switch to list layout' },
        { value: 'set layout grid', description: 'Switch to grid layout' },
        { value: 'set layout compact', description: 'Switch to compact layout' },
        { value: 'group by role', description: 'Group by role' },
      ],
    };

    const subcommands = subcommandMap[lowerCommand] || [];

    // Filter subcommands based on partial match
    if (!lowerPartial) {
      return subcommands.map(sub => ({
        value: sub.value,
        description: sub.description,
        hasChildren: false,
        fullCommand: sub.value,
        category: 'style' as CommandCategory
      }));
    }

    const filtered = subcommands.filter(sub => {
      const subcommandPart = sub.value.split(' ').slice(1).join(' ');
      return subcommandPart.startsWith(lowerPartial) || subcommandPart.includes(lowerPartial);
    });

    return filtered.map(sub => ({
      value: sub.value,
      description: sub.description,
      hasChildren: false,
      fullCommand: sub.value,
      category: 'style' as CommandCategory
    }));
  }

  /**
   * Get progressive command segments based on current input
   * Returns all commands that match the current input prefix
   * Supports intelligent multi-word matching (e.g., "add chart" matches "add line chart")
   */
  getProgressiveSegments(input: string): CommandSegment[] {
    const trimmed = input.trim();

    // Remove leading slash if present
    const withoutSlash = trimmed.startsWith('/') ? trimmed.substring(1) : trimmed;
    const searchPrefix = withoutSlash.toLowerCase();
    const searchWords = searchPrefix.split(' ').filter(w => w.length > 0);

    // Special handling for /add and /+ commands - show component type suggestions
    if (searchWords.length >= 1 && (searchWords[0] === 'add' || searchWords[0] === '+')) {
      return this.getComponentTypeSuggestions(searchWords.slice(1).join(' '));
    }

    // Special handling for /navigate commands - show page suggestions
    const navigateAliases = ['navigate', 'nav', 'goto', 'link'];
    if (searchWords.length >= 1 && navigateAliases.includes(searchWords[0])) {
      return this.getPageSuggestions(searchWords.slice(1).join(' '));
    }

    // Special handling for commands with subcommands
    const subcommandCommands = ['border', 'bd', 'shadow', 'sh', 'hover', 'hv', 'font', 'ft', 'file-storage', 'fs', 'files', 'kanban-board', 'gantt-chart', 'gantt', 'calendar', 'questionnaire', 'voting-card', 'modal', 'checklist', 'horizontal-timeline', 'timeline', 'assignment-widget'];
    if (searchWords.length >= 1 && subcommandCommands.includes(searchWords[0])) {
      return this.getSubcommands(searchWords[0], searchWords.slice(1).join(' '));
    }

    // Get all commands and aliases
    const allCommands: CommandSegment[] = [];
    const seen = new Set<string>();

    // Helper function to check if a command matches the search
    const matchesSearch = (commandName: string): { matches: boolean; priority: number } => {
      const lowerCommand = commandName.toLowerCase();

      // Exact match - highest priority
      if (lowerCommand === searchPrefix) {
        return { matches: true, priority: 0 };
      }

      // Starts with search prefix - high priority
      if (lowerCommand.startsWith(searchPrefix)) {
        return { matches: true, priority: 1 };
      }

      // Multi-word partial matching
      if (searchWords.length > 1) {
        const commandWords = lowerCommand.split(' ');

        // Check if command starts with the first search word(s)
        // and contains the remaining search words
        let matchesPartial = true;
        let matchedWords = 0;

        // First word(s) must match from the start
        for (let i = 0; i < searchWords.length - 1 && i < commandWords.length; i++) {
          if (!commandWords[i].startsWith(searchWords[i])) {
            matchesPartial = false;
            break;
          }
          matchedWords++;
        }

        // Last search word can appear anywhere in remaining command words
        if (matchesPartial && matchedWords === searchWords.length - 1) {
          const lastSearchWord = searchWords[searchWords.length - 1];
          const remainingCommandWords = commandWords.slice(matchedWords);
          const hasLastWord = remainingCommandWords.some(w => w.startsWith(lastSearchWord));

          if (hasLastWord) {
            return { matches: true, priority: 2 };
          }
        }
      }

      return { matches: false, priority: 999 };
    };

    // Add main commands with match results
    const commandMatches: Array<{ segment: CommandSegment; priority: number }> = [];

    this.commands.forEach((command, name) => {
      const matchResult = matchesSearch(name);
      if (matchResult.matches && !seen.has(name)) {
        seen.add(name);
        commandMatches.push({
          segment: {
            value: name,
            description: command.description,
            hasChildren: false,
            fullCommand: name,
            category: command.category
          },
          priority: matchResult.priority
        });
      }
    });

    // Also check aliases
    this.aliases.forEach((targetName, alias) => {
      const matchResult = matchesSearch(alias);
      const targetCommand = this.commands.get(targetName);
      if (matchResult.matches && targetCommand && !seen.has(alias)) {
        seen.add(alias);
        commandMatches.push({
          segment: {
            value: alias,
            description: targetCommand.description,
            hasChildren: false,
            fullCommand: alias,
            category: targetCommand.category
          },
          priority: matchResult.priority
        });
      }
    });

    // Sort by priority, then alphabetically
    commandMatches.sort((a, b) => {
      // First by priority (lower is better)
      if (a.priority !== b.priority) {
        return a.priority - b.priority;
      }
      // Then alphabetically
      return a.segment.value.localeCompare(b.segment.value);
    });

    return commandMatches.map(m => m.segment);
  }


  /**
   * Get all commands for autocomplete
   */
  getAllCommands(): Array<{ name: string; description: string; category: CommandCategory }> {
    const commands: Array<{ name: string; description: string; category: CommandCategory }> = [];

    this.commands.forEach(command => {
      commands.push({
        name: command.name,
        description: command.description,
        category: command.category
      });
    });

    return commands.sort((a, b) => a.name.localeCompare(b.name));
  }

  /**
   * Get commands filtered by category
   */
  getCommandsByCategory(category: CommandCategory): SlashCommand[] {
    const commands: SlashCommand[] = [];

    this.commands.forEach(command => {
      if (command.category === category) {
        commands.push(command);
      }
    });

    return commands;
  }
}

export const slashCommandSystem = SlashCommandSystem.getInstance();
